/**
 * Global SweetAlert2 Configuration Utility
 *
 * This utility provides consistent styling and configuration for all SweetAlert dialogs.
 * It does NOT modify business logic - only visual appearance.
 *
 * Usage:
 * import { getSwalConfig } from '@/utils/swal-config';
 *
 * Swal.fire({
 *   ...getSwalConfig('success'),
 *   title: 'Success!',
 *   text: 'Your action was completed.'
 * });
 */

/**
 * Get consistent SweetAlert configuration based on type
 * @param {string} type - The type of alert: 'success', 'error', 'warning', 'info', 'question', 'confirm'
 * @param {object} customOptions - Additional custom options to merge
 * @returns {object} SweetAlert configuration object
 */
export function getSwalConfig(type = "default", customOptions = {}) {
  const baseConfig = {
    backdrop: "rgba(0, 0, 0, 0.45)",
    allowOutsideClick: true,
    allowEscapeKey: true,
    showClass: {
      popup: "swal2-show",
      backdrop: "swal2-backdrop-show",
    },
    hideClass: {
      popup: "swal2-hide",
      backdrop: "swal2-backdrop-hide",
    },
  };

  const typeConfigs = {
    success: {
      icon: "success",
      confirmButtonColor: "#10b981",
      customClass: {
        popup: "premium-success elegant-style",
      },
    },
    error: {
      icon: "error",
      confirmButtonColor: "#ef4444",
      customClass: {
        popup: "premium-error elegant-style",
      },
    },
    warning: {
      icon: "warning",
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#6b7280",
      customClass: {
        popup: "premium-warning elegant-style",
      },
    },
    info: {
      icon: "info",
      confirmButtonColor: "#3b82f6",
      customClass: {
        popup: "elegant-style",
      },
    },
    question: {
      icon: "question",
      confirmButtonColor: "#8b5cf6",
      cancelButtonColor: "#6b7280",
      customClass: {
        popup: "elegant-style",
      },
    },
    confirm: {
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      customClass: {
        popup: "elegant-style",
      },
    },
    default: {
      customClass: {
        popup: "elegant-style",
      },
    },
  };

  const config = typeConfigs[type] || typeConfigs.default;

  return {
    ...baseConfig,
    ...config,
    ...customOptions,
  };
}

/**
 * Get compact style configuration (smaller dialogs)
 */
export function getCompactSwalConfig(type = "default", customOptions = {}) {
  const config = getSwalConfig(type, customOptions);

  return {
    ...config,
    customClass: {
      ...config.customClass,
      popup: `${config.customClass?.popup || ""} compact-style`.trim(),
    },
  };
}

/**
 * Get wide style configuration (larger dialogs)
 */
export function getWideSwalConfig(type = "default", customOptions = {}) {
  const config = getSwalConfig(type, customOptions);

  return {
    ...config,
    customClass: {
      ...config.customClass,
      popup: `${config.customClass?.popup || ""} wide-style`.trim(),
    },
  };
}

/**
 * Preset configurations for common scenarios
 */
export const SwalPresets = {
  // Success message
  success: (title, text) => ({
    ...getSwalConfig("success"),
    title,
    text,
  }),

  // Error message
  error: (title, text) => ({
    ...getSwalConfig("error"),
    title,
    text,
  }),

  // Warning message
  warning: (title, text) => ({
    ...getSwalConfig("warning"),
    title,
    text,
  }),

  // Info message
  info: (title, text) => ({
    ...getSwalConfig("info"),
    title,
    text,
  }),

  // Confirmation dialog
  confirm: (title, text, confirmText = "Confirm", cancelText = "Cancel") => ({
    ...getSwalConfig("confirm"),
    title,
    text,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
  }),

  // Delete confirmation
  deleteConfirm: (
    title = "Are you sure?",
    text = "This action cannot be undone."
  ) => ({
    ...getSwalConfig("warning"),
    title,
    text,
    showCancelButton: true,
    confirmButtonText: "Delete",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#6b7280",
  }),

  // Loading state
  loading: (title = "Please wait...", text = "Processing your request") => ({
    ...getSwalConfig("default"),
    title,
    text,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => {
      Swal.showLoading();
    },
  }),
};

export default {
  getSwalConfig,
  getCompactSwalConfig,
  getWideSwalConfig,
  SwalPresets,
};
